import { SMS_LINK_PREFIX } from "./constants";

/** SMS·운영 링크용 공개 호스트 (프로토콜 제외) */
export function getSupportAppPublicHost() {
  const fromEnv = (
    typeof process !== "undefined" && process.env?.REACT_APP_PUBLIC_ORIGIN
      ? process.env.REACT_APP_PUBLIC_ORIGIN
      : "https://mfm.119survey.org"
  )
    .replace(/\/$/, "")
    .replace(/^https?:\/\//, "");

  if (typeof window !== "undefined" && window.location?.hostname === "localhost") {
    return fromEnv;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/^https?:\/\//, "");
  }
  return fromEnv;
}

/** QR·화면용 전체 URL (dsnm·rname 포함) */
export function buildSupportAppLink({
  dsid,
  rdvId,
  siteId,
  dsnm = "",
  rname = "",
  type = "other",
}) {
  const host = getSupportAppPublicHost();
  if (!host || !dsid) return "";

  const isSiteMode = siteId != null && siteId !== "" && Number(siteId) > 0;
  const idParam = isSiteMode
    ? `site_id=${encodeURIComponent(siteId)}`
    : `rdv_id=${encodeURIComponent(rdvId ?? "")}`;

  const params = new URLSearchParams({
    dsid: String(dsid),
    type: String(type),
  });
  if (isSiteMode) params.set("site_id", String(siteId));
  else if (rdvId != null && rdvId !== "") params.set("rdv_id", String(rdvId));
  if (dsnm) params.set("dsnm", String(dsnm));
  if (rname) params.set("rname", String(rname));

  return `https://${host}/qrattendmgr?${params.toString()}`;
}

/** SMS용 짧은 경로 (/qra → App.js에서 /qrattendmgr 리다이렉트) */
const SMS_ATTEND_PATH = "/qra";

/** URL에 출퇴근 snum(a/att) 포함 여부 */
export function smsLinkHasAttParam(link) {
  const q = String(link || "").split("?")[1] || "";
  return /(?:^|&)(?:a|att)=/i.test(q);
}

/** SMS용 URL — 80자 이내 최대한 a=출퇴근 snum 포함 (등록정보 연동) */
export function buildSupportAppSmsLink({
  dsid,
  rdvId,
  siteId,
  type = "other",
  attSnum,
}) {
  const host = getSupportAppPublicHost();
  if (!host || !dsid) return "";

  const isSiteMode = siteId != null && siteId !== "" && Number(siteId) > 0;
  const att =
    attSnum != null && attSnum !== "" ? String(attSnum) : "";

  const candidates = [];
  const paths = [SMS_ATTEND_PATH, "/qrattendmgr"];

  if (att) {
    for (const path of paths) {
      if (isSiteMode) {
        candidates.push(
          `${host}${path}?dsid=${encodeURIComponent(dsid)}&site_id=${encodeURIComponent(siteId)}&t=other&a=${encodeURIComponent(att)}`,
          `${host}${path}?dsid=${encodeURIComponent(dsid)}&site_id=${encodeURIComponent(siteId)}&type=other&a=${encodeURIComponent(att)}`,
        );
      } else if (rdvId != null && rdvId !== "") {
        candidates.push(
          `${host}${path}?dsid=${encodeURIComponent(dsid)}&rdv_id=${encodeURIComponent(rdvId)}&t=other&a=${encodeURIComponent(att)}`,
          `${host}${path}?dsid=${encodeURIComponent(dsid)}&rdv_id=${encodeURIComponent(rdvId)}&type=other&a=${encodeURIComponent(att)}`,
        );
      }
      candidates.push(
        `${host}${path}?dsid=${encodeURIComponent(dsid)}&a=${encodeURIComponent(att)}&t=other`,
      );
    }
  }

  for (const path of paths) {
    if (isSiteMode) {
      candidates.push(
        `${host}${path}?dsid=${encodeURIComponent(dsid)}&site_id=${encodeURIComponent(siteId)}&type=${encodeURIComponent(type)}`,
      );
    } else {
      candidates.push(
        `${host}${path}?dsid=${encodeURIComponent(dsid)}&rdv_id=${encodeURIComponent(rdvId ?? "")}&type=${encodeURIComponent(type)}`,
      );
    }
  }

  for (const url of candidates) {
    if (!isSmsMessageTooLong(buildSmsLinkMessage(url))) {
      return url;
    }
  }
  return candidates[candidates.length - 1];
}

/** 출퇴근 행 → 집결지/현장 ID (RdvAttendPage 연동용) */
export function locationFromAttendanceRow(row) {
  const attendRdvId = Number(row?.rdv_id);
  if (!Number.isFinite(attendRdvId)) {
    return { isSiteMode: false, rdvId: null, siteId: null };
  }
  if (attendRdvId < 0) {
    return { isSiteMode: true, siteId: Math.abs(attendRdvId), rdvId: null };
  }
  return { isSiteMode: false, rdvId: attendRdvId, siteId: null };
}

/** 긴·짧은 쿼리 파라미터 통합 파싱 (d/dsid, a/att, r/rdv_id, s/site_id) */
export function parseSupportAttendQuery(searchParams, pathname = "") {
  const dsid = (searchParams.get("dsid") || searchParams.get("d") || "").trim();
  const att = (searchParams.get("att") || searchParams.get("a") || "").trim();
  const isShortPath =
    String(pathname || "").includes("/qra") ||
    String(pathname || "").includes("/qrattendmgr");
  const type =
    searchParams.get("type") ||
    searchParams.get("t") ||
    (att ? "other" : isShortPath ? "other" : "");

  const rdvIdParam = searchParams.get("rdv_id") ?? searchParams.get("r");
  const rdvId =
    rdvIdParam != null && rdvIdParam !== ""
      ? parseInt(rdvIdParam, 10)
      : null;
  const siteIdParam = searchParams.get("site_id") ?? searchParams.get("s");
  const siteId =
    siteIdParam != null && siteIdParam !== ""
      ? parseInt(siteIdParam, 10)
      : null;
  const isSiteMode = !Number.isNaN(siteId) && siteId > 0;

  return {
    dsid,
    att,
    type,
    rdvId: Number.isNaN(rdvId) ? null : rdvId,
    siteId: Number.isNaN(siteId) ? null : siteId,
    isSiteMode,
    dsnm: searchParams.get("dsnm") || "",
    rname: searchParams.get("rname") || "",
    isAttOnlyMode: Boolean(
      dsid && att && !isSiteMode && !(rdvId != null && rdvId > 0),
    ),
  };
}

export function buildSupportAppLinkFromWorkplace({
  dsid,
  dsnm,
  workplaceName,
  rdvId,
  siteId,
  forSms = false,
  attSnum,
}) {
  if (forSms) {
    if (siteId != null && Number(siteId) > 0) {
      return buildSupportAppSmsLink({ dsid, siteId, type: "other", attSnum });
    }
    return buildSupportAppSmsLink({ dsid, rdvId, type: "other", attSnum });
  }

  if (siteId != null && Number(siteId) > 0) {
    return buildSupportAppLink({
      dsid,
      siteId,
      dsnm,
      rname: workplaceName,
      type: "other",
    });
  }
  return buildSupportAppLink({
    dsid,
    rdvId,
    dsnm,
    rname: workplaceName,
    type: "other",
  });
}

/** 동원자원앱 문자와 유사한 한 줄 형식 (SDK_SMS_SEND SMS_MSG 길이 제한 대응) */
export function buildSmsLinkMessage(link) {
  const url = String(link || "").trim();
  if (!url) return SMS_LINK_PREFIX;
  return `${SMS_LINK_PREFIX} ${url}`;
}

/** 발송 전 길이 확인용 */
export function measureSmsLinkMessage(link) {
  const msg = buildSmsLinkMessage(link);
  return { msg, len: msg.length, tooLong: msg.length > SMS_MSG_MAX_LEN };
}

export const SMS_MSG_MAX_LEN = 80;

export function isSmsMessageTooLong(text) {
  return String(text ?? "").length > SMS_MSG_MAX_LEN;
}
